import { Link } from 'react-router-dom';
import { Coffee, MapPin, Phone, Clock, Instagram, Mail } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-[#4A3B32] text-[#F5F1E8] pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#E07A5F] rounded-full flex items-center justify-center">
                <Coffee className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-2xl" style={{ fontFamily: 'Fraunces, serif' }}>
                A1 Cafe
              </span>
            </div>
            <p className="text-[#F5F1E8]/80 leading-relaxed mb-4">
              Your go-to café near NITK. Quality food at student-friendly prices.
            </p>
            {/* Order platforms */}
            <div className="flex items-center gap-3">
              <a 
                href="https://www.swiggy.com/menu/1242857?source=sharing" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white rounded-lg px-2 py-1 hover:scale-105 transition-transform"
                data-testid="footer-swiggy"
              >
                <img src="https://customer-assets.emergentagent.com/job_f7742170-5b8f-447f-9d8a-e99e46f1b0c0/artifacts/dfsl17ui_images.jfif" alt="Swiggy" className="h-5 w-5 object-contain" />
              </a>
              <a 
                href="https://zomato.onelink.me/xqzv/rcql1v1i" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white rounded-lg px-2 py-1 hover:scale-105 transition-transform"
                data-testid="footer-zomato"
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Zomato_logo.png/80px-Zomato_logo.png" alt="Zomato" className="h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
              Quick Links
            </h4>
            <nav className="flex flex-col gap-3">
              <Link to="/" className="text-[#F5F1E8]/80 hover:text-[#E07A5F] transition-colors" data-testid="footer-link-home">
                Home
              </Link>
              <Link to="/menu" className="text-[#F5F1E8]/80 hover:text-[#E07A5F] transition-colors" data-testid="footer-link-menu">
                Our Menu
              </Link>
              <Link to="/gallery" className="text-[#F5F1E8]/80 hover:text-[#E07A5F] transition-colors" data-testid="footer-link-gallery">
                Gallery
              </Link>
              <Link to="/about" className="text-[#F5F1E8]/80 hover:text-[#E07A5F] transition-colors" data-testid="footer-link-about">
                About Us
              </Link>
              <Link to="/contact" className="text-[#F5F1E8]/80 hover:text-[#E07A5F] transition-colors" data-testid="footer-link-contact">
                Contact
              </Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
              Contact Us
            </h4>
            <div className="flex flex-col gap-4">
              <a href="tel:+919611475342" className="flex items-center gap-3 text-[#F5F1E8]/80 hover:text-[#E07A5F] transition-colors" data-testid="footer-phone">
                <Phone className="w-5 h-5 text-[#E07A5F]" />
                +91 96114 75342
              </a>
              <a href="mailto:shettyrains40@gmail.com" className="flex items-center gap-3 text-[#F5F1E8]/80 hover:text-[#E07A5F] transition-colors" data-testid="footer-email">
                <Mail className="w-5 h-5 text-[#E07A5F]" />
                shettyrains40@gmail.com
              </a>
              <a 
                href="https://maps.app.goo.gl/jgFwpAgEZtdiEADL6" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-[#F5F1E8]/80 hover:text-[#E07A5F] transition-colors"
              >
                <MapPin className="w-5 h-5 text-[#E07A5F] flex-shrink-0 mt-0.5" />
                <span>
                  NIT Karnataka, Hostel Area,<br />
                  Near NITK Campus,<br />
                  Surathkal, Mangaluru
                </span>
              </a>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-bold text-lg mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
              Opening Hours
            </h4>
            <div className="flex items-start gap-3 text-[#F5F1E8]/80 mb-6">
              <Clock className="w-5 h-5 text-[#E07A5F] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[#F5F1E8]">Every Day</p>
                <p>11:45 AM – 10:00 PM</p>
              </div>
            </div>
            
            {/* Social */}
            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/a1cafenitk?igsh=MTU5Yno1ZGgxb3BjMQ=="
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#E07A5F] rounded-full flex items-center justify-center hover:bg-[#F5F1E8] hover:text-[#4A3B32] transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://wa.me/919611475342"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center hover:bg-[#1da851] transition-colors"
                aria-label="WhatsApp"
                data-testid="footer-whatsapp"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#F5F1E8]/20 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#F5F1E8]/60 text-sm">
            © {new Date().getFullYear()} A1 Cafe. Made with ❤️ for NITK students.
          </p>
          <p className="text-[#F5F1E8]/60 text-sm font-accent text-xl">
            "Good food, Good mood!"
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
